<template>
  <div class="sidebar">
    <div class="sidebar__head">
      <app-brand-mark :size="22" wordmark />
      <app-icon-button
        icon="panel"
        label="Seitenleiste einklappen"
        :size="32"
        :boxed="false"
        :tooltip="$shell.popupOffen ? false : 'bottom'"
        class="sidebar__toggle"
        data-shell-toggle
        @click="collapse"
      />
    </div>

    <!-- Liste scrollt bei wenig Höhe, das Profil bleibt darunter stehen -->
    <div ref="scroll" class="sidebar__scroll">
      <nav class="sidebar__nav" aria-label="Hauptnavigation">
        <ul class="sidebar__list">
          <li v-for="group in groups" :key="group.id">
            <router-link
              v-if="!group.children"
              :to="group.path"
              class="sidebar__link"
              :aria-current="isActivePath(group.path) ? 'page' : null"
            >
              <app-icon
                :name="group.icon"
                :size="18"
                class="sidebar__icon"
                :class="{ 'sidebar__icon--here': isActivePath(group.path) }"
              />
              <span class="sidebar__label">{{ group.title }}</span>
            </router-link>
            <template v-else>
              <button
                type="button"
                class="sidebar__group"
                :class="{ 'sidebar__group--here': isHereClosed(group) }"
                :aria-expanded="isOpen(group.id) ? 'true' : 'false'"
                :aria-controls="subId(group.id)"
                :aria-current="isHereClosed(group) ? 'true' : null"
                @click="toggle(group.id)"
              >
                <app-icon
                  :name="group.icon"
                  :size="18"
                  class="sidebar__icon"
                  :class="{ 'sidebar__icon--here': group.id === activeGroupId }"
                />
                <span class="sidebar__label">{{ group.title }}</span>
                <app-icon :name="isOpen(group.id) ? 'chevD' : 'chevR'" :size="14" class="sidebar__chev" />
              </button>
              <ul v-show="isOpen(group.id)" :id="subId(group.id)" class="sidebar__sub">
                <li v-for="entry in group.children" :key="entry.path">
                  <router-link
                    :to="entry.path"
                    class="sidebar__sublink"
                    :aria-current="isActivePath(entry.path) ? 'page' : null"
                  >{{ entry.title }}</router-link>
                </li>
              </ul>
            </template>
          </li>
        </ul>
      </nav>
    </div>

    <div class="sidebar__foot">
      <app-user-menu placement="right" :nudge="8" @sign-out="$emit('sign-out')">
        <template v-slot:activator="{ on, attrs }">
          <button type="button" class="sidebar__user" v-bind="attrs" aria-haspopup="menu" :title="userTitle" v-on="on">
            <app-avatar :name="name" :size="28" ring />
            <span class="sidebar__who">
              <span class="sidebar__name">{{ name }}</span>
              <span v-if="login" class="sidebar__login">{{ login }}</span>
            </span>
            <app-icon name="chevUD" :size="14" class="sidebar__chev" />
          </button>
        </template>
      </app-user-menu>
    </div>
  </div>
</template>

<script>
// Seitenleiste 232 (Maus, ab 1264; Spezifikation 2.11, gen_r3.py:737–800): oben links das SIMA-Logo wie auf
// simasoftware.de (oranger Stern und Schriftzug in der Markenschrift), rechts „Seitenleiste einklappen“; darunter die
// Hauptnavigation mit aufklappbaren Gruppen (scrollt bei wenig Höhe). Unten der Benutzerknopf (oben der Name, darunter
// der Benutzername) mit Benutzermenü.
// Fluchten: Icons, Stern und Avatar auf x = 30 (wie in der Rail), Texte ab 49 (Schriftzug ebenso), rechte Achse 208
// (Chevrons, Einklappen). Kopfzeile auf der Linie des Seitenkopfs; das Profil steht unten so weit innen wie das Logo oben.
// Offene Gruppen: die Gruppe der aktiven Seite öffnet automatisch (nicht gemerkt); weitere öffnet/schließt der Nutzer
// per Klick, gemerkt in localStorage['navGruppen'] (shellState).
import AppBrandMark from './AppBrandMark.vue';
import AppUserMenu from './AppUserMenu.vue';
import { normalizePath, userDisplayName, userLoginName } from '../../shell/navigation';
import { isGroupOpen, toggleGroup, setAutoGroup, setSidebarCollapsed } from '../../shell/shellState';

export default {
  name: 'AppSidebar',
  components: { AppBrandMark, AppUserMenu },
  props: {
    groups: { type: Array, required: true },
    activePath: { type: String, default: '' },
    activeGroupId: { type: String, default: null }
  },
  computed: {
    profile() {
      return this.$store.state.profile;
    },
    name() {
      return userDisplayName(this.profile);
    },
    login() {
      return userLoginName(this.profile);
    },
    // voller Text bei gekürztem Namen
    userTitle() {
      return [this.name, this.login].filter(Boolean).join(' · ');
    }
  },
  watch: {
    activePath: {
      immediate: true,
      handler() {
        const id = this.activeGroupId;
        setAutoGroup(id && this.groups.some(group => group.id === id && group.children) ? id : null);
        this.$nextTick(this.revealActive);
      }
    }
  },
  methods: {
    isActivePath(path) {
      return normalizePath(path) === this.activePath;
    },
    isOpen(id) {
      // shell.openGroups und shell.autoGroup sind reaktiv; isGroupOpen liest beide
      return isGroupOpen(id);
    },
    // aktive Gruppe zugeklappt: der Gruppenknopf zeigt den Ort
    isHereClosed(group) {
      return group.id === this.activeGroupId && !this.isOpen(group.id);
    },
    toggle(id) {
      toggleGroup(id);
    },
    subId(id) {
      return 'shell-nav-' + id;
    },
    // aktive Seite in kurzen Fenstern in den sichtbaren Teil der Liste holen
    revealActive() {
      const scroll = this.$refs.scroll;
      const here = scroll ? scroll.querySelector('[aria-current="page"], [aria-current="true"]') : null;
      if (here && typeof here.scrollIntoView === 'function') {
        here.scrollIntoView({ block: 'nearest' });
      }
    },
    collapse(event) {
      setSidebarCollapsed(true);
      // per Tastatur ausgelöst (detail 0): der Knopf verschwindet, Fokus auf „Seitenleiste ausklappen“ der Rail
      if (event && event.detail === 0) {
        this.$nextTick(() => {
          const toggle = document.querySelector('.shell-nav [data-shell-toggle]');
          if (toggle) {
            toggle.focus();
          }
        });
      }
    }
  }
};
</script>

<style scoped>
/* feste Breite: der Inhalt bleibt stehen, während Vuetify die Breite des Drawers animiert. Oben 21 = 8 Abstand des
   Blatts + 1 Kante + 12 Polster des Seitenkopfs: die Kopfzeile liegt auf der Zeile des Seitenkopfs. Unten 18: der
   Avatar (28 im Knopf 44) endet 26 über der Fensterkante, so wie der Stern oben 26 darunter beginnt; das Profil sitzt
   damit spiegelbildlich zum Logo innerhalb der Höhe des Blatts. */
.sidebar {
  width: 232px;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 21px 8px 18px;
  color: var(--ink);
  font-family: var(--font);
}

/* Kopf 32 wie die Zeile des Seitenkopfs: links Stern 22 (Mitte x = 30) und Schriftzug ab 49, rechts Einklappen
   (Icon-Mitte 208 wie die Chevrons) */
.sidebar__head {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  margin-bottom: 12px;
  padding-left: 11px;
}
.sidebar__head .sidebar__toggle {
  color: var(--ink-3);
}
:where(html:not(.dichte-touch)) .sidebar__head .sidebar__toggle:not(:disabled):hover {
  background: var(--seg-hov);
  color: var(--ink);
}
.sidebar__head .sidebar__toggle:not(:disabled):active {
  background: var(--seg-press);
}

/* Liste in einem Scrollbereich; Scrollschatten oben und unten nur, wenn dort Inhalt verdeckt ist
   (Abdeckung wandert mit dem Inhalt, Schatten bleibt am Rand); 4 px Innenraum, damit Fokusringe nicht abschneiden */
.sidebar__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: -4px;
  padding: 4px;
  background:
    linear-gradient(var(--chrome) 30%, transparent) top / 100% 24px no-repeat local,
    linear-gradient(transparent, var(--chrome) 70%) bottom / 100% 24px no-repeat local,
    radial-gradient(farthest-side at 50% 0, var(--sh-scroll), transparent) top / 100% 8px no-repeat scroll,
    radial-gradient(farthest-side at 50% 100%, var(--sh-scroll), transparent) bottom / 100% 8px no-repeat scroll;
  background-color: var(--chrome);
}
.sidebar__nav {
  flex: none;
}
.sidebar__list,
.sidebar__sub {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
/* Unterliste: Führungslinie auf der Icon-Mitte (30), Text bündig mit den Gruppentiteln bei 49 */
.sidebar__sub {
  margin: 4px 0 4px 21px;
  padding-left: 11px;
  border-left: 1px solid var(--sheet);
}

.sidebar__link,
.sidebar__group,
.sidebar__sublink {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  margin: 0;
  border-radius: var(--r-ctl);
  background: transparent;
  color: var(--ink-2);
  font-family: var(--font);
  font-size: 13px;
  line-height: 18px;
  letter-spacing: normal;
  text-align: left;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
/* Hauptpunkt 32: Kante transparent (nichts springt), Icon-Mitte 30, Text 49 */
.sidebar__link {
  height: 32px;
  gap: 10px;
  padding: 0 9px 0 12px;
  border: 1px solid transparent;
  font-weight: 500;
}
/* Gruppenknopf 32: randlos, Icon-Mitte 30, Chevron auf der rechten Achse 208 */
.sidebar__group {
  height: 32px;
  gap: 10px;
  padding: 0 9px 0 13px;
  border: 0;
  font-weight: 500;
}
/* Unterpunkt 28 ohne Icon: klarer zweiter Rhythmus unter den 32er-Punkten */
.sidebar__sublink {
  height: 28px;
  padding: 0 7px;
  border: 1px solid transparent;
  font-weight: 400;
}
.sidebar__label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sidebar__sublink {
  overflow: hidden;
  text-overflow: ellipsis;
}
.sidebar__icon {
  flex: none;
  color: var(--ink-3);
}
/* Ort: Icon der aktiven Gruppe bzw. des aktiven Hauptpunkts orange */
.sidebar__icon.sidebar__icon--here {
  color: var(--acc);
}
.sidebar__chev {
  flex: none;
  color: var(--ink-3);
}

/* Zustände: Hover nur Maus → aktiv → gedrückt. Aktiv ist eine graue Fläche (--here: hell eine Stufe dunkler als
   Hover, dunkel die hellste Fläche) mit fetter Schrift (Kundenwunsch 12.10.2026), ohne Kante und Schatten: der Ort,
   kein Knopf (die Knöpfe im Blatt tragen weiche Kante und Schatten). Die zugeklappte aktive Gruppe zeigt den Ort
   ebenso. */
:where(html:not(.dichte-touch)) .sidebar__link:hover,
:where(html:not(.dichte-touch)) .sidebar__group:hover,
:where(html:not(.dichte-touch)) .sidebar__sublink:hover {
  background: var(--seg-hov);
}
.sidebar__link[aria-current="page"],
.sidebar__sublink[aria-current="page"],
.sidebar__group.sidebar__group--here {
  background: var(--here);
  color: var(--ink);
  font-weight: 700;
}
.sidebar__link:active,
.sidebar__group:active,
.sidebar__sublink:active {
  background: var(--seg-press);
}

/* Benutzerblock ohne Trennlinie: Avatar-Mitte 30, Name und darunter Benutzername ab 49, Chevron auf 208 */
.sidebar__foot {
  flex: none;
  margin-top: 8px;
}
.sidebar__user {
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 0;
  padding: 0 9px 0 8px;
  border: 0;
  border-radius: var(--r-ctl);
  background: transparent;
  color: var(--ink);
  font-family: var(--font);
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
:where(html:not(.dichte-touch)) .sidebar__user:hover,
.sidebar__user[aria-expanded="true"] {
  background: var(--seg-hov);
}
.sidebar__user:active {
  background: var(--seg-press);
}
.sidebar__who {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.sidebar__name,
.sidebar__login {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sidebar__name {
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
}
.sidebar__login {
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
</style>
